export const disease3Module = {
  id: "disease3",
  title: "Kidney Diseases: Stones, Infections & Tumours",
  sections: [
    {
      type: "text",
      title: "Nephrolithiasis: Stone Types",
      content: `
        <p><span class="glossary-term">Nephrolithiasis</span> (kidney stones) affects ~10% of the population. Understanding the stone type guides prevention and treatment.</p>

        <div class="highlight-box">
          <p><strong>Calcium oxalate (75–80%) — most common</strong></p>
          <ul>
            <li><strong>Radiopaque</strong>; envelope/dumbbell-shaped crystals</li>
            <li>Risk factors: hypercalciuria, hyperoxaluria, hypocitraturia, low urine volume</li>
            <li>Hyperoxaluria: Crohn's/ileal disease (fat malabsorption → free oxalate absorbed), primary hyperoxaluria, excess vitamin C</li>
            <li>Hypocitraturia: Type 1 RTA, acetazolamide</li>
            <li>Treatment: hydration (&gt;2.5 L/day), thiazides (↓urinary Ca²⁺), potassium citrate (↑citrate + alkalinise)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Uric acid (5–10%) — only radiolucent common stone</strong></p>
          <ul>
            <li><span class="purple-term">Radiolucent</span> — invisible on plain X-ray but visible on CT</li>
            <li>Risk: gout, obesity, DM, myeloproliferative disorders (tumour lysis), Lesch-Nyhan syndrome, acidic urine (pH &lt;5.5), dehydration</li>
            <li>Treatment: urinary alkalinisation (K-citrate or NaHCO₃ to pH &gt;6.5 — can dissolve existing stones), allopurinol, hydration</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Struvite / magnesium ammonium phosphate (10–15%)</strong></p>
          <ul>
            <li>Radiopaque; <strong>staghorn calculi</strong> (fill the renal pelvis); coffin-lid crystals</li>
            <li>Cause: urease-producing bacteria (Proteus, Klebsiella, Pseudomonas, S. aureus) split urea → ↑ammonia → alkaline urine → struvite precipitation</li>
            <li>Always associated with <strong>recurrent UTIs</strong></li>
            <li>Treatment: eradicate infection (antibiotics), PCNL (percutaneous nephrolithotomy), acetohydroxamic acid (urease inhibitor)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Cystine (1–2%)</strong></p>
          <ul>
            <li>Autosomal recessive; SLC3A1/SLC7A9 gene mutation → defective PCT transport of COLA (Cystine, Ornithine, Lysine, Arginine)</li>
            <li>Hexagonal crystals; weakly radiopaque</li>
            <li>Nitroprusside test: pink/purple colour (positive for cystinuria)</li>
            <li>Treatment: alkalinise urine to pH &gt;7.5, high fluid intake (&gt;3 L/day), D-penicillamine or tiopronin (chelate cystine)</li>
          </ul>
        </div>

        <div class="mnemonic-card">
          <div class="mnemonic-title">Radiolucent vs Radiopaque</div>
          <div class="mnemonic-content"><strong>Radiolucent</strong> (invisible on plain X-ray): Uric acid stones, drug stones (indinavir)<br/><strong>Radiopaque</strong> (visible on plain X-ray): Calcium oxalate, calcium phosphate, struvite, cystine (faintly)</div>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient with Crohn's disease affecting the terminal ileum develops recurrent calcium oxalate kidney stones. What is the mechanism linking ileal disease to oxalate stones?",
      options: [
        "Crohn's disease causes hypercalciuria by impairing calcium absorption",
        "Fat malabsorption leads to free fatty acids binding calcium in the gut, leaving more free oxalate to be absorbed in the colon",
        "Ileal inflammation releases oxalic acid directly into the bloodstream",
        "Crohn's disease causes urine alkalinisation, promoting calcium oxalate precipitation",
      ],
      correct: 1,
      explanation:
        "In terminal ileal disease (Crohn's, ileal resection), fat is malabsorbed. Unabsorbed fatty acids bind calcium in the gut lumen, preventing calcium from binding dietary oxalate. Free oxalate is then absorbed from the colon (enteric hyperoxaluria) → hyperoxaluria → calcium oxalate stones. Treatment includes a low-oxalate diet, calcium supplements with meals (to bind oxalate in the gut), cholestyramine (binds bile salts/oxalate), and hydration.",
    },
    {
      type: "question",
      text: "A patient with recurrent kidney stones passes a stone. X-ray shows the stone is invisible. Urine pH is 4.8. Which stone type is most likely and what is the treatment to dissolve existing stones?",
      options: [
        "Struvite — antibiotics to eradicate urease-producing organisms",
        "Uric acid — urinary alkalinisation with potassium citrate to pH >6.5",
        "Calcium oxalate — thiazide diuretics to reduce urinary calcium",
        "Cystine — D-penicillamine to chelate cystine",
      ],
      correct: 1,
      explanation:
        "Uric acid stones are the only common kidney stones that are radiolucent (invisible on plain X-ray, visible on CT). They form in acidic urine (pH <5.5). Urinary alkalinisation with potassium citrate (or sodium bicarbonate) to pH >6.5 increases uric acid solubility and can both prevent new stones and dissolve existing ones — making uric acid the only stone type that can be medically dissolved. Allopurinol reduces uric acid production.",
    },
    {
      type: "question",
      text: "A patient with recurrent UTIs is found to have a staghorn calculus. Which organism is the most common cause and what is the mechanism of stone formation?",
      options: [
        "E. coli — produces acid urine causing calcium precipitation",
        "Proteus mirabilis — urease splits urea into ammonia, alkalinising urine and promoting magnesium ammonium phosphate precipitation",
        "Staphylococcus aureus — produces a toxin that inhibits calcium reabsorption",
        "Enterococcus faecalis — reduces urinary citrate, promoting crystal nucleation",
      ],
      correct: 1,
      explanation:
        "Struvite (magnesium ammonium phosphate) stones are caused by urease-producing bacteria, most commonly Proteus mirabilis. Urease splits urea → ammonia (NH₄⁺) → alkaline urine (pH >7.0) → precipitation of Mg²⁺ + NH₄⁺ + PO₄³⁻ as struvite. Staghorn calculi fill the renal pelvis and calyces and can cause obstructive nephropathy and sepsis. Treatment requires both antibiotics and surgical removal (PCNL); antibiotics alone cannot eliminate the stone.",
    },
    {
      type: "text",
      title: "Nephrolithiasis: Clinical Management",
      content: `
        <p>Kidney stones cause <strong>colicky loin-to-groin pain</strong> (ureteral spasm as the stone migrates), haematuria, nausea, and vomiting. There is no peritonism (distinguishes from surgical abdomen).</p>

        <div class="highlight-box">
          <p><strong>Investigation</strong></p>
          <ul>
            <li><strong>NCCT KUB</strong> (non-contrast CT of kidneys, ureters, bladder) — gold standard; detects all stones including radiolucent; also identifies alternative diagnoses</li>
            <li>Plain X-ray KUB — only radiopaque stones; misses 40% of stones; cheap screening</li>
            <li>Urine dipstick — haematuria (90%), nitrites if infected</li>
            <li>Metabolic workup for recurrent stones: 24-hour urine (calcium, oxalate, urate, citrate, volume), serum calcium, PTH, uric acid</li>
          </ul>
        </div>

        <p><strong>Infected obstructed stone = urological emergency</strong> — obstructed ureter + infected urine → sepsis → renal damage. Requires emergency drainage (ureteric stent or percutaneous nephrostomy) + IV antibiotics regardless of stone size.</p>

        <div class="highlight-box">
          <p><strong>Management by stone size</strong></p>
          <ul>
            <li><strong>&lt;5 mm</strong>: ~95% pass spontaneously; analgesia (NSAIDs first-line &gt; opioids) + alpha-blocker (<span class="purple-term">tamsulosin</span> — medical expulsive therapy, relaxes ureteral smooth muscle)</li>
            <li><strong>5–10 mm</strong>: ~50% pass; conservative initially, intervene if not passing</li>
            <li><strong>&gt;10 mm or upper urinary tract</strong>: active intervention</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Interventional options</strong></p>
          <ul>
            <li><strong>ESWL</strong> (extracorporeal shock wave lithotripsy) — &lt;2 cm stones, non-obstructed, good renal function</li>
            <li><strong>Ureteroscopy + laser lithotripsy</strong> — ureteral stones, lower pole renal stones, failed ESWL</li>
            <li><strong>PCNL</strong> (percutaneous nephrolithotomy) — stones &gt;2 cm, staghorn calculi, ESWL failures</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A patient presents with right loin-to-groin pain and microscopic haematuria. NCCT shows an 8 mm stone at the right vesicoureteric junction. There is no fever or signs of infection. What is the most appropriate initial management?",
      options: [
        "Emergency PCNL — any stone >5 mm requires immediate surgical removal",
        "Conservative management with analgesia (NSAIDs) and tamsulosin (alpha-blocker for medical expulsive therapy)",
        "ESWL immediately — all stones >5 mm require lithotripsy",
        "Immediate ureteroscopy — VUJ stones always require surgical intervention",
      ],
      correct: 1,
      explanation:
        "An 8 mm stone at the vesicoureteric junction (VUJ) without signs of infection can be managed conservatively initially. Tamsulosin (alpha-1 blocker) relaxes the smooth muscle of the distal ureter (medical expulsive therapy), facilitating spontaneous passage. NSAIDs are preferred for analgesia (also reduce renal pelvic pressure). However, intervention (ureteroscopy) is indicated if conservative management fails after 4 weeks, or sooner if there is pain that is refractory, AKI, or infection.",
    },
    {
      type: "text",
      title: "Urinary Tract Infections",
      content: `
        <p>UTIs are the most common bacterial infection in women. Understanding when to treat, which antibiotic to use, and when to investigate is key clinically.</p>

        <div class="highlight-box">
          <p><strong>Common organisms</strong></p>
          <ul>
            <li><strong>E. coli</strong> (80%) — most common; fimbriae allow bladder wall adhesion</li>
            <li><strong>Staphylococcus saprophyticus</strong> — young sexually active women; novobiocin-resistant</li>
            <li><strong>Klebsiella pneumoniae</strong> — especially diabetics, hospitalised patients</li>
            <li><strong>Proteus mirabilis</strong> — alkaline urine, struvite stones, fishy smell</li>
            <li><strong>Enterococcus faecalis</strong> — hospital-acquired, post-instrumentation</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Uncomplicated cystitis (women)</strong></p>
          <ul>
            <li>Dysuria, frequency, urgency, suprapubic pain; no systemic features</li>
            <li>Diagnosis: clinical; urine dipstick (nitrites + leucocytes &gt;90% sensitive)</li>
            <li>Treatment: trimethoprim 200 mg BD × 7 days, or nitrofurantoin 100 mg MR BD × 5 days</li>
            <li>Avoid trimethoprim in first trimester (folate antagonist) and nitrofurantoin in late pregnancy/GFR &lt;30</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Acute pyelonephritis</strong></p>
          <ul>
            <li>Fever &gt;38°C, rigors, loin pain + CVAT, nausea; LUTS present</li>
            <li>Urine: WBC casts, bacteriuria; blood: ↑WBC, ↑CRP</li>
            <li>Treatment (mild-moderate): oral co-amoxiclav or ciprofloxacin 7–14 days</li>
            <li>Treatment (severe/septic): IV piperacillin-tazobactam or gentamicin; switch to oral when afebrile 24–48 hours</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Asymptomatic bacteriuria (ASB)</strong></p>
          <ul>
            <li>Only treat in: <span class="purple-term">pregnancy</span> (risk of ascending pyelonephritis and preterm labour) and <span class="purple-term">before urological procedures</span></li>
            <li>Do NOT treat in: catheterised patients (unless symptomatic), diabetics, elderly, premenopausal women</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Recurrent UTIs (≥2/6 months or ≥3/year)</strong></p>
          <ul>
            <li>Behavioural measures: adequate hydration, post-coital voiding, avoid spermicides</li>
            <li>Post-coital prophylaxis (single-dose trimethoprim or nitrofurantoin)</li>
            <li>Continuous low-dose prophylaxis (nitrofurantoin 50 mg or trimethoprim 100 mg at night)</li>
            <li>Topical oestrogen (post-menopausal women): restores urogenital epithelium</li>
          </ul>
        </div>
      `,
    },
    {
      type: "question",
      text: "A 28-year-old pregnant woman at 20 weeks gestation has a routine urine culture showing >10⁵ E. coli per mL but has no symptoms. What is the appropriate management?",
      options: [
        "Reassure and repeat culture in 4 weeks — asymptomatic bacteriuria in pregnancy does not require treatment",
        "Treat with antibiotics — asymptomatic bacteriuria in pregnancy significantly increases the risk of pyelonephritis and preterm labour",
        "Treat only if symptoms develop — routine treatment increases antibiotic resistance",
        "Refer to urology — recurrent bacteriuria in pregnancy requires cystoscopy",
      ],
      correct: 1,
      explanation:
        "Asymptomatic bacteriuria (ASB) in pregnancy must be treated because hormonal changes cause ureteral dilatation and urinary stasis, significantly increasing the risk of ascending pyelonephritis (~25–40% of untreated ASB progresses to pyelonephritis). Pyelonephritis in pregnancy is associated with preterm labour, low birth weight, and maternal sepsis. Treat with nitrofurantoin (first/second trimester) or cefalexin; avoid trimethoprim (first trimester) and nitrofurantoin (term).",
    },
    {
      type: "question",
      text: "Which bacterial species is classically associated with UTI in young sexually active women and is distinguished from E. coli by its novobiocin resistance?",
      options: [
        "Proteus mirabilis",
        "Staphylococcus saprophyticus",
        "Klebsiella pneumoniae",
        "Enterococcus faecalis",
      ],
      correct: 1,
      explanation:
        "Staphylococcus saprophyticus is the second most common cause of uncomplicated UTI in young sexually active women (after E. coli). It is coagulase-negative and novobiocin-resistant (this distinguishes it from S. epidermidis which is novobiocin-sensitive). It has adhesins that promote urogenital tract colonisation. Treatment: nitrofurantoin or trimethoprim.",
    },
    {
      type: "text",
      title: "Renal Cell Carcinoma (RCC)",
      content: `
        <p><span class="glossary-term">RCC</span> arises from renal tubular epithelium and is the most common solid renal tumour in adults, with peak incidence in the 6th–7th decade. It is now usually detected incidentally on imaging.</p>

        <div class="highlight-box">
          <p><strong>Subtypes</strong></p>
          <ul>
            <li><strong>Clear cell (75%)</strong>: golden-yellow, lipid-laden cells; <span class="purple-term">VHL gene mutation</span> (chromosome 3p25); associated with von Hippel-Lindau syndrome (bilateral, early onset); VEGF/mTOR pathway driven; worst prognosis</li>
            <li><strong>Papillary (15%)</strong>: Type 1 (MET gene mutation, hereditary papillary RCC) and Type 2 (FH gene mutation); basophilic/eosinophilic papillary architecture; better prognosis than clear cell</li>
            <li><strong>Chromophobe (5%)</strong>: BHD gene (Birt-Hogg-Dubé syndrome); perinuclear halo; best prognosis of the three main types</li>
            <li><strong>Oncocytoma</strong> (benign): mahogany-brown, central stellate scar; "spoke wheel" vascularity on angiography; cannot reliably distinguish from RCC on imaging → usually excised</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Clinical features</strong></p>
          <ul>
            <li><strong>Classic triad</strong> (only ~10%): haematuria + flank pain + palpable mass</li>
            <li>Most common presentation: painless haematuria or incidental finding</li>
            <li><strong>Left-sided varicocele</strong> that does not decompress lying down → suspect left renal vein invasion (left gonadal vein drains into left renal vein)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Paraneoplastic syndromes</strong> (RCC is notable for these)</p>
          <ul>
            <li>Polycythaemia — ectopic EPO production</li>
            <li>Hypercalcaemia — PTHrP or bony metastases</li>
            <li><span class="purple-term">Stauffer's syndrome</span> — non-metastatic hepatic dysfunction (↑ALP, ↑bilirubin) without liver metastases; resolves after nephrectomy</li>
            <li>Hypertension — ectopic renin</li>
            <li>Amyloidosis, Cushing's (ectopic ACTH)</li>
          </ul>
        </div>

        <p><strong>Treatment</strong>: Localised — partial or radical nephrectomy (laparoscopic preferred). Metastatic — VEGF-R TKIs (sunitinib, pazopanib, cabozantinib), immune checkpoint inhibitors (nivolumab, pembrolizumab), combination IO+TKI; mTOR inhibitors (everolimus) second-line. RCC is chemo- and radio-resistant.</p>
      `,
    },
    {
      type: "question",
      text: "A patient with RCC is found to have elevated ALP and bilirubin, but liver imaging shows no metastases. What syndrome does this represent and what happens after nephrectomy?",
      options: [
        "Malignant hypercalcaemia — caused by PTHrP secretion",
        "Stauffer's syndrome — non-metastatic hepatic dysfunction that resolves after nephrectomy",
        "Paraneoplastic polycythaemia — ectopic EPO causing secondary hepatomegaly",
        "Cholestasis from IVC compression by tumour",
      ],
      correct: 1,
      explanation:
        "Stauffer's syndrome is a paraneoplastic manifestation of RCC characterised by non-metastatic hepatic dysfunction — elevated liver enzymes and bilirubin without evidence of liver metastases on imaging. The mechanism is thought to involve cytokine production (IL-6) by the tumour affecting hepatic function. Importantly, it typically resolves after nephrectomy, confirming its paraneoplastic nature. Recurrence of Stauffer's syndrome after nephrectomy suggests tumour relapse.",
    },
    {
      type: "question",
      text: "Which germline mutation causes bilateral, early-onset clear cell RCC and is associated with haemangioblastomas and phaeochromocytomas?",
      options: [
        "MET gene mutation — hereditary papillary RCC",
        "VHL gene mutation — von Hippel-Lindau disease",
        "BRCA2 mutation — hereditary breast and ovarian cancer",
        "BHD gene mutation — Birt-Hogg-Dubé syndrome",
      ],
      correct: 1,
      explanation:
        "Von Hippel-Lindau (VHL) disease is caused by loss-of-function mutation of the VHL tumour suppressor gene on chromosome 3p25. VHL normally targets HIF-1α for degradation; loss → constitutive HIF-1α activation → ↑VEGF and other growth factors. This causes bilateral, multifocal clear cell RCC (early onset ~40s), haemangioblastomas (cerebellum, retina, spinal cord), phaeochromocytomas, and pancreatic cysts. VHL mutations are also found in ~75% of sporadic clear cell RCC.",
    },
    {
      type: "text",
      title: "Urothelial Carcinoma & Wilms' Tumour",
      content: `
        <p><span class="glossary-term">Urothelial (transitional cell) carcinoma (TCC)</span> arises from the urothelium lining the renal pelvis, ureters, bladder, and urethra. The bladder is the most common site (90% of TCCs).</p>

        <div class="highlight-box">
          <p><strong>Risk factors for urothelial carcinoma — mnemonic "SCAN"</strong></p>
          <ul>
            <li><strong>S</strong>moking — most important (2–6× ↑risk)</li>
            <li><strong>C</strong>hemical exposure — aniline dyes (aromatic amines in rubber/paint industry), cyclophosphamide (metabolite acrolein is directly toxic to urothelium)</li>
            <li><strong>A</strong>nalgesic abuse — phenacetin nephropathy (upper tract TCC)</li>
            <li><strong>N</strong>ematode — Schistosoma haematobium (squamous cell carcinoma of bladder, not TCC)</li>
            <li>Also: Lynch syndrome (MSH2/MLH1), pelvic radiation, chronic UTI</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Field cancerisation</strong></p>
          <ul>
            <li>The entire urothelium is exposed to the same carcinogens → synchronous or metachronous lesions at multiple sites</li>
            <li>Upper tract TCC: always exclude synchronous bladder lesion (CT urogram + cystoscopy)</li>
            <li>After bladder TCC: surveillance for upper tract involvement</li>
          </ul>
        </div>

        <p><strong>Treatment</strong>: Non-muscle invasive (Ta, T1, CIS) → TURBT (transurethral resection) + intravesical BCG (carcinoma in situ) or mitomycin; regular cystoscopic surveillance. Muscle-invasive (≥T2) → radical cystectomy + neoadjuvant cisplatin-based chemotherapy. Metastatic → gemcitabine + cisplatin; pembrolizumab (second-line).</p>

        <hr/>

        <p><span class="glossary-term">Wilms' tumour (nephroblastoma)</span> is the most common renal malignancy in children, peaking at age 3–4 years.</p>

        <div class="highlight-box">
          <p><strong>Clinical features</strong></p>
          <ul>
            <li>Painless abdominal mass (most common presentation), haematuria, hypertension</li>
            <li><strong>Does not cross the midline</strong> (distinguishes from neuroblastoma which typically does)</li>
          </ul>
        </div>

        <div class="highlight-box">
          <p><strong>Associated syndromes</strong></p>
          <ul>
            <li><strong>WAGR syndrome</strong> — WT1 deletion (11p13): Wilms', Aniridia, Genitourinary anomalies, mental Retardation</li>
            <li><strong>Beckwith-Wiedemann syndrome</strong> — WT2/IGF2 (11p15): macroglossia, hemihypertrophy, omphalocele, overgrowth</li>
            <li><strong>Denys-Drash syndrome</strong> — WT1 point mutation: ambiguous genitalia + diffuse mesangial sclerosis + Wilms'</li>
          </ul>
        </div>

        <p><strong>Pathology</strong>: Triphasic tumour (blastemal + epithelial + stromal components). Bilateral in 5–10%.</p>
        <p><strong>Prognosis</strong>: Excellent — &gt;90% 5-year survival with nephrectomy + chemotherapy ± radiotherapy. Favourable histology (no anaplasia) responds very well.</p>
      `,
    },
    {
      type: "question",
      text: "A factory worker who has worked with aniline dyes for 20 years presents with painless haematuria. Cystoscopy reveals a papillary lesion in the bladder. What is the most likely diagnosis and what occupational exposure is responsible?",
      options: [
        "RCC — arsenic exposure in the workplace",
        "Urothelial (transitional cell) carcinoma — aromatic amine (aniline dye) exposure",
        "Squamous cell carcinoma — schistosomiasis from occupational water exposure",
        "Nephroblastoma — organic solvent exposure",
      ],
      correct: 1,
      explanation:
        "Aniline dyes contain aromatic amines (2-naphthylamine, benzidine), which are well-established bladder carcinogens. These are metabolised in the liver, conjugated, and excreted in urine. In the bladder, urinary glucuronidase cleaves the conjugate, releasing the active carcinogen in direct contact with the urothelium. Workers in the rubber, dye, paint, and printing industries are at elevated risk. Smoking is the most common cause overall; occupational aromatic amine exposure is the most important occupational risk factor.",
    },
    {
      type: "question",
      text: "A 3-year-old boy presents with a large left-sided abdominal mass. It is smooth, does not cross the midline, and is associated with unilateral aniridia. What is the most likely diagnosis and what genetic syndrome should be suspected?",
      options: [
        "Neuroblastoma — associated with NF1 and N-MYC amplification",
        "Wilms' tumour — associated with WAGR syndrome (WT1 deletion on chromosome 11p13)",
        "Hepatoblastoma — associated with familial adenomatous polyposis",
        "Renal cell carcinoma — associated with VHL syndrome",
      ],
      correct: 1,
      explanation:
        "Wilms' tumour (nephroblastoma) is the most common renal malignancy in children (peak age 3–4 years). Key features: painless abdominal mass that does NOT cross the midline. Aniridia (absent iris) in association with Wilms' suggests WAGR syndrome — deletion of chromosome 11p13 removing both the WT1 gene and the PAX6 gene (causing aniridia). Neuroblastoma typically presents with a harder mass that commonly crosses the midline and may show periorbital ecchymoses (raccoon eyes).",
    },
    {
      type: "question",
      text: "Why does a left-sided RCC with renal vein invasion sometimes cause a left varicocele that does not decompress in the supine position?",
      options: [
        "The tumour compresses the left ureter, causing hydronephrosis and elevated venous pressure",
        "The left gonadal vein drains into the left renal vein; invasion/compression of the left renal vein obstructs gonadal venous drainage",
        "RCC produces aldosterone, causing fluid retention and venous engorgement",
        "The right renal vein drains into the IVC, making left-sided RCC more likely to cause varicocele",
      ],
      correct: 1,
      explanation:
        "The left gonadal (testicular or ovarian) vein drains into the left renal vein at a 90° angle (unlike the right gonadal vein which drains directly into the IVC). When left RCC invades or compresses the left renal vein, venous return from the left gonad is obstructed → left varicocele. The key clinical sign is that this varicocele does NOT decompress when the patient lies down (postural non-drainage), which distinguishes neoplastic obstruction from a primary varicocele. This is a red flag sign in an adult male.",
    },
  ],
};
